import { parseISO } from 'date-fns';
import { z } from 'zod';

import {
  isDate,
  isPostalCode,
  isUUID,
  isValidPhoneNumber,
} from '@/utils/helpers';

const nullableId = z
  .string()
  .refine((val: string) => isUUID(val), 'Invalid value')
  .nullable();

const emailSchema = z.union([z.literal(''), z.string().email().nullable()]);

export const contactPhoneSchema = (allowFreeText: boolean) =>
  allowFreeText
    ? z.string().max(50, 'Must be at most 50 characters long.').nullable()
    : z
        .string()
        .refine(
          (phone) => (phone ? isValidPhoneNumber(phone) : true),
          'Must be a phone number.',
        )
        .nullable();

export const contactEmailSchema = (allowFreeText: boolean) =>
  allowFreeText
    ? z.string().max(100, 'Must be at most 100 characters long.').nullable()
    : emailSchema;

export type ShipmentSchema = z.infer<ReturnType<typeof createShipmentSchema>>;

const SHIPPED = 100000000;
const TRANSFER_TRIP = 100000003;
export const INCOMPLETE = 100000007;

export function createShipmentSchema({
  hasCalves,
  hasDonors,
  hasEmbryos,
  hasRecipients,
  hasSemen,
  enforceContentMatch = true,
  requireShipmentStatus = false,
  requireShippingMethod = false,
  requireShipFromSite = false,
  allowContactFreeText = false,
}: {
  hasEmbryos: boolean;
  hasSemen: boolean;
  hasDonors: boolean;
  hasRecipients: boolean;
  hasCalves: boolean;
  enforceContentMatch?: boolean;
  requireShipmentStatus?: boolean;
  requireShippingMethod?: boolean;
  requireShipFromSite?: boolean;
  allowContactFreeText?: boolean;
}) {
  const shipFromSiteSchema = z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable();

  return z
    .object({
      date: z
        .string()
        .refine((value) => {
          return isDate(new Date(value));
        })
        .nullable(),

      needByDate: z
        .string()
        .refine((value) => {
          return isDate(parseISO(value));
        })
        .nullable(),
      originalNeedByDate: z
        .string()
        .refine((value) => {
          return isDate(new Date(value));
        })
        .nullable(),
      amount: z.number().min(0).nullable(),
      tank: z
        .string()
        .max(20, 'Must be at most 20 characters long.')
        .nullable(),
      trackingNumber: z.string().nullable(),
      requestedBy: z.string().nullable(),
      phone: contactPhoneSchema(allowContactFreeText),
      email: contactEmailSchema(allowContactFreeText),
      billTo: z.string().min(1, 'Required'),
      billStatus: z.number().nullable(),
      comment: z.string().nullable(),
      shipName: z.string().nullable(),
      shipAddress: z.string().nullable(),
      shipAddress2: z.string().nullable(),
      shipAddress3: z.string().nullable(),
      shipCity: z.string().nullable(),
      shipState: z.string().nullable(),
      shipZip: z
        .string()
        .nullable()
        .refine(
          (value) => (value ? isPostalCode(value) : true),
          'Must be a valid postal code',
        ),
      callTag: z.string().nullable(),
      shipmentCategory: z.number().nullable(),
      shipmentStatus: requireShipmentStatus
        ? z
            .number({ required_error: 'Shipment Status is required.' })
            .int()
            .nullable()
            .refine(
              (value): boolean => value !== null,
              'Shipment Status is required.',
            )
        : z.number().nullable(),
      shippingMethod: requireShippingMethod
        ? z
            .number({ required_error: 'Shipping Method is required.' })
            .int()
            .nullable()
            .refine(
              (value): boolean => value !== null,
              'Shipping Method is required.',
            )
        : z.number().nullable(),
      hasEmbryos: enforceContentMatch
        ? z
            .boolean()
            .nullable()
            .refine((value) => !!value === hasEmbryos, 'Mismatch with embryos')
        : z.boolean().nullable(),
      hasSemen: enforceContentMatch
        ? z
            .boolean()
            .nullable()
            .refine((value) => !!value === hasSemen, 'Mismatch with semen')
        : z.boolean().nullable(),
      hasDonors: enforceContentMatch
        ? z
            .boolean()
            .nullable()
            .refine((value) => !!value === hasDonors, 'Mismatch with donors')
        : z.boolean().nullable(),
      hasRecipients: enforceContentMatch
        ? z
            .boolean()
            .nullable()
            .refine(
              (value) => !!value === hasRecipients,
              'Mismatch with recipients',
            )
        : z.boolean().nullable(),
      hasCalves: enforceContentMatch
        ? z
            .boolean()
            .nullable()
            .refine((value) => !!value === hasCalves, 'Mismatch with calves')
        : z.boolean().nullable(),
      ownerId: nullableId,
      owner: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      shipToId: nullableId,
      shipTo: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      packedById: nullableId,
      packedBy: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      shipFromSiteId: nullableId,
      shipFromSite: requireShipFromSite
        ? shipFromSiteSchema.refine(
            (value): boolean => value !== null,
            'Ship From Site is required.',
          )
        : shipFromSiteSchema,
    })
    .superRefine((data, ctx) => {
      if (
        (data.shipmentStatus === SHIPPED ||
          data.shipmentStatus === TRANSFER_TRIP) &&
        !data.packedById
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Packed By is required when status is Shipped or Transfer Trip.',
          path: ['packedById'],
        });
      }
    });
}
